import { IMPERSONATION_FLAG } from '../../api/axios.instance';

/**
 * Paytics "GA4 Sync" (a Stripe app) sends Stripe's purchase, renewal, trial and cancellation
 * events to GA4 by Measurement Protocol. Without this page script they carry no GA client id and
 * land as "direct"; with it Paytics maps the visitor's GA client and session to the Stripe
 * Customer, so those events credit the campaign that brought the visitor.
 *
 * session.js reads `window.paytics` once, about a second after it loads, and posts the mapping only
 * if a Customer id is there by then. So it is loaded per Checkout, after the Customer id is pushed —
 * not at startup, where it would find nothing. Production only, and never while a superadmin
 * impersonates: their browser must not be mapped to the customer.
 */
const PAYTICS_SCRIPT_URL = 'https://paytics.io/session.js?id=oKBaOAk7VwvMG3Xmo7zdW5gNZeE0QDmr';

/** session.js waits 1,024 ms before reading the queue; a redirect sooner would cancel the post. */
const PAYTICS_POST_DELAY_MS = 2_000;

const isPayticsEnabled = () =>
  import.meta.env.VITE_ENV === 'production' && sessionStorage.getItem(IMPERSONATION_FLAG) !== '1';

/** The K3X user id (GA4 `user_id`) Paytics sends along with the mapping. */
export const identifyPayticsUser = (userId: string) => {
  if (!isPayticsEnabled()) {
    return;
  }

  // session.js takes the first entry with a `user_id`, so a different user replaces it.
  window.paytics = [
    ...(window.paytics ?? []).filter((entry) => !entry.user_id),
    { user_id: userId },
  ];
};

export const resetPayticsIdentity = () => {
  window.paytics = [];
  delete window._payticsStripeCustomerIdCache;
};

/**
 * Maps this visitor to the Stripe Customer a Checkout is about to subscribe. Resolves once
 * session.js has had time to post, so a redirect to Stripe's hosted page can wait for it; the
 * embedded form stays on the page and need not wait.
 */
export const linkPayticsStripeCustomer = (stripeCustomerId: string): Promise<void> => {
  if (!isPayticsEnabled()) {
    return Promise.resolve();
  }

  window.paytics = [
    ...(window.paytics ?? []).filter((entry) => !entry.stripe_customer_id),
    { stripe_customer_id: stripeCustomerId },
  ];
  // session.js keeps the first Customer id it finds for the page; another Checkout may differ.
  delete window._payticsStripeCustomerIdCache;

  const script = document.createElement('script');
  script.async = true;
  script.src = PAYTICS_SCRIPT_URL;
  document.head.appendChild(script);

  return new Promise((resolve) => setTimeout(resolve, PAYTICS_POST_DELAY_MS));
};
